/**
 * safePrint.js - Secure print utilities
 * Uses DOMPurify for HTML sanitization and safe DOM manipulation
 * NO document.write or innerHTML assignments without sanitization
 */

import DOMPurify from 'dompurify';
import { logger } from './logger';

// Configure DOMPurify to be extra strict
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr',
    'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'div', 'span', 'a', 'img', 'strong', 'em', 'b', 'i', 'u',
    'blockquote', 'pre', 'code', 'small', 'sub', 'sup'
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'class', 'id', 'style',
    'width', 'height', 'colspan', 'rowspan', 'scope'
  ],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onclick', 'onerror', 'onload', 'onmouseover', 'onfocus', 'onblur', 'onsubmit']
};

/**
 * Escapes HTML special characters to prevent XSS
 * Uses textContent which is inherently safe
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text safe for display
 */
export const escapeHtml = (text) => {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = String(text);
  return div.textContent; // Return the escaped text, not innerHTML
};

/**
 * Sanitizes HTML content using DOMPurify
 * @param {string} html - HTML to sanitize
 * @returns {string} - Sanitized HTML safe for rendering
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, PURIFY_CONFIG);
};

/**
 * Safely sets content on a DOM element
 * Uses DOMPurify to sanitize before setting
 * @param {HTMLElement} element - Target element
 * @param {string} html - HTML content to set
 */
const safeSetContent = (element, html) => {
  if (!element) return;
  const sanitized = DOMPurify.sanitize(html, PURIFY_CONFIG);
  // Use DOMPurify's sanitized output
  element.innerHTML = sanitized;
};

/**
 * Creates a complete HTML document structure safely
 * @param {Document} doc - Document to populate
 * @param {Object} options - Document options
 */
const buildDocumentSafely = (doc, { title, content, theme = 'dark' }) => {
  // Clear existing content
  while (doc.documentElement.firstChild) {
    doc.documentElement.removeChild(doc.documentElement.firstChild);
  }
  
  // Create head
  const head = doc.createElement('head');
  doc.documentElement.appendChild(head);
  
  // Meta charset
  const charset = doc.createElement('meta');
  charset.setAttribute('charset', 'utf-8');
  head.appendChild(charset);
  
  // Title (safe - using textContent)
  const titleEl = doc.createElement('title');
  titleEl.textContent = escapeHtml(title);
  head.appendChild(titleEl);
  
  // Styles (safe - hardcoded CSS)
  const style = doc.createElement('style');
  style.textContent = theme === 'dark' ? `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: system-ui, -apple-system, sans-serif; 
      padding: 20px; 
      background: #1a1a2e; 
      color: white; 
    }
    h1 { color: #00dcff; margin-bottom: 20px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { padding: 8px; text-align: left; border: 1px solid #333; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    @media print { 
      body { background: white; color: black; } 
      h1 { color: #0066cc; }
    }
  ` : `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: system-ui, -apple-system, sans-serif; 
      padding: 20px; 
      background: white; 
      color: black; 
    }
    h1 { color: #333; margin-bottom: 20px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { padding: 8px; text-align: left; border: 1px solid #ddd; }
  `;
  head.appendChild(style);
  
  // Create body
  const body = doc.createElement('body');
  doc.documentElement.appendChild(body);
  
  // Title heading (safe - using textContent)
  const h1 = doc.createElement('h1');
  h1.textContent = escapeHtml(title);
  body.appendChild(h1);
  
  // Content container (sanitized)
  const contentDiv = doc.createElement('div');
  contentDiv.className = 'print-content';
  safeSetContent(contentDiv, content);
  body.appendChild(contentDiv);
  
  return doc;
};

/**
 * Opens a print window with safe DOM manipulation
 * @param {Object} options - Print options
 * @param {string} options.title - Window title
 * @param {string} options.content - HTML content to print (will be sanitized)
 * @param {string} [options.theme='dark'] - 'dark' or 'light'
 * @returns {Window|null} - Print window reference
 */
export const openSafePrintWindow = ({ title, content, theme = 'dark' }) => {
  try {
    const printWindow = window.open('about:blank', '_blank', 'width=800,height=600');
    if (!printWindow) {
      logger.error('Popup blocked - cannot open print window');
      return null;
    }

    const doc = printWindow.document;
    
    // Set document type
    doc.open();
    doc.close();
    
    // Set HTML lang attribute
    doc.documentElement.lang = 'es';
    
    // Build document safely
    buildDocumentSafely(doc, { title, content, theme });
    
    // Trigger print after content is rendered
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
    
    return printWindow;
  } catch (error) {
    logger.error('Error opening print window:', error);
    return null;
  }
};

/**
 * Prints content using a hidden iframe (no popup)
 * @param {Object} options - Print options
 * @param {string} options.title - Print title
 * @param {string} options.content - HTML content to print
 */
export const printContentInPlace = ({ title, content }) => {
  try {
    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:none;visibility:hidden;';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      logger.error('Could not access iframe document');
      document.body.removeChild(iframe);
      return;
    }
    
    // Initialize document
    doc.open();
    doc.close();
    
    // Build document safely
    buildDocumentSafely(doc, { title, content, theme: 'light' });
    
    // Print and cleanup
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      
      // Remove iframe after printing
      setTimeout(() => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      }, 1000);
    }, 300);
  } catch (error) {
    logger.error('Error printing content:', error);
  }
};

/**
 * Creates a safe HTML string for image content
 * @param {string} imgSrc - Image source (data URL or URL)
 * @param {string} alt - Alt text
 * @returns {string} - Safe HTML string
 */
export const createSafeImageHtml = (imgSrc, alt = '') => {
  // Validate image source - only allow data URLs and https URLs
  const isValidSrc = imgSrc && (
    imgSrc.startsWith('data:image/') ||
    imgSrc.startsWith('https://') ||
    imgSrc.startsWith('/')
  );
  
  if (!isValidSrc) {
    logger.warn('Invalid image source blocked:', imgSrc?.substring(0, 50));
    return '';
  }
  
  // Create element safely
  const img = document.createElement('img');
  img.src = imgSrc;
  img.alt = escapeHtml(alt);
  img.style.cssText = 'max-width:100%;height:auto;';
  
  // Return outer HTML (safe because we built it programmatically)
  return img.outerHTML;
};

export default {
  escapeHtml,
  sanitizeHtml,
  openSafePrintWindow,
  printContentInPlace,
  createSafeImageHtml
};
